import { useEffect } from 'react';
import { SearchQuery, SearchResults, SearchFacetOptions } from '#server/models';
import { trpc } from '@/data-loaders';
import { trackGAEvent } from '@/services';
import { useFilterStore, useSortOrderStore, useAvailabilityStore } from '@/store';
import { convertActiveFiltersToSearchFilters } from '@/utils';

const getInitialFacetCounts = (): SearchResults['facetResultCounts'] =>
  SearchFacetOptions.reduce(
    (acc, facet) => {
      acc[facet] = null;
      return acc;
    },
    {} as SearchResults['facetResultCounts'],
  );

export const useSearch = (params: SearchQuery & { enabled?: boolean }) => {
  const { currentFilterSet, activeFilters, region } = useFilterStore();
  const { activeSortOrder } = useSortOrderStore();
  const { activeAvailability } = useAvailabilityStore();

  const searchFilters =
    activeFilters && currentFilterSet
      ? convertActiveFiltersToSearchFilters(activeFilters, currentFilterSet)
      : undefined;

  const queryParams: SearchQuery = {
    region: region,
    searchTerms: params.searchTerms,
    facet: params.facet,
    page: params.page,
    pageSize: params.pageSize,
    sortOrder: activeSortOrder ?? undefined,
    availability: activeAvailability ?? undefined,
    searchFilters: searchFilters,
  };

  const { data, error, isFetching } = trpc.search.useQuery(queryParams, {
    enabled: params.enabled ?? true,
    placeholderData: (previousData) => {
      return {
        facetResultCounts:
          !!params.searchTerms && previousData?.facetResultCounts
            ? previousData.facetResultCounts
            : getInitialFacetCounts(),
        results: undefined,
        resultCount: params.facet === previousData?.activeFacet ? previousData?.resultCount : undefined,
        page: params.facet === previousData?.activeFacet ? previousData?.page : undefined,
        pageSize: params.facet === previousData?.activeFacet ? previousData?.pageSize : undefined,
        didYouMean: undefined,
        loading: true,
      };
    },
    retry: false,
  });

  useEffect(() => {
    if (error && !isFetching) {
      if (!navigator.onLine) {
        // Browser confirms device has no network — client connectivity issue,
        // not a system failure so not reported to GA
      } else {
        trackGAEvent({
          event: 'search_error',
          search_term: params.searchTerms,
          search_facet: params.facet,
          search_error_message: error.message,
          search_error_count: 1,
        });
      }
    }
  }, [error, isFetching, params.searchTerms, params.facet]);

  return {
    results: data,
    error,
  };
};
